import type { AxiosInstance } from 'axios'
import type {
    ICreateMessageDTO,
    ICreateMessageFromParentMessageDTO,
    IGirdMessageItem,
    IMessageDetails,
    ISendMessageDTO,
} from '@/typings/message'
import { NO_CONTENT_CODE } from '@/config/httpCodes.ts'
import type { IGridPayloadRequest } from '@/types/list'
import type { IAttachmentToDownload, IFileInBase64, IGridResponse } from '@/typings/api'
import { buildQueryForGrid } from '@/helpers/grid.ts'

export default (axios: AxiosInstance) => ({
    createMessage: async (
        payload: ICreateMessageDTO,
        subAccountId: string,
    ): Promise<{ messageId: string }> => {
        const { data } = await axios.post<{ messageId: string }>(
            `/guardian/${subAccountId}/message/messages`,
            payload,
        )

        return data
    },

    createMessageFromParent: async (
        payload: ICreateMessageFromParentMessageDTO,
        subAccountId: string,
    ): Promise<{ messageId: string }> => {
        const { data } = await axios.post<{ messageId: string }>(
            `/guardian/${subAccountId}/message/messages/from-parent`,
            payload,
        )

        return data
    },

    changeDraftMessage: async ({
        guardianMessageId,
        payload,
        subAccountId,
    }: {
        payload: ISendMessageDTO
        subAccountId: string
        guardianMessageId: string
    }): Promise<boolean> => {
        const { status } = await axios.put(
            `/guardian/${subAccountId}/message/messages/${guardianMessageId}`,
            payload,
        )

        return status === NO_CONTENT_CODE
    },

    sendMessage: async ({
        guardianMessageId,
        payload,
        subAccountId,
    }: {
        payload: ISendMessageDTO
        subAccountId: string
        guardianMessageId: string
    }): Promise<boolean> => {
        const { status } = await axios.put(
            `/guardian/${subAccountId}/message/messages/${guardianMessageId}/send`,
            payload,
        )

        return status === NO_CONTENT_CODE
    },

    removeDraftMessage: async ({
        guardianMessageId,
        subAccountId,
        version,
    }: {
        subAccountId: string
        guardianMessageId: string
        version: number
    }): Promise<boolean> => {
        const { status } = await axios.delete(
            `/guardian/${subAccountId}/message/messages/${guardianMessageId}`,
            {
                params: {
                    version,
                },
            },
        )

        return status === NO_CONTENT_CODE
    },

    markAsReadMessage: async ({
        guardianMessageId,
        subAccountId,
    }: {
        subAccountId: string
        guardianMessageId: string
    }): Promise<boolean> => {
        const { status } = await axios.put(
            `/guardian/${subAccountId}/message/messages/${guardianMessageId}/mark-as-read`,
        )

        return status === NO_CONTENT_CODE
    },

    markAsUnreadMessage: async ({
        guardianMessageId,
        subAccountId,
    }: {
        subAccountId: string
        guardianMessageId: string
    }): Promise<boolean> => {
        const { status } = await axios.put(
            `/guardian/${subAccountId}/message/messages/${guardianMessageId}/mark-as-unread`,
        )

        return status === NO_CONTENT_CODE
    },

    getMessageDetailsById: async ({
        guardianMessageId,
        subAccountId,
    }: {
        subAccountId: string
        guardianMessageId: string
    }): Promise<IMessageDetails> => {
        const { data } = await axios.get<IMessageDetails>(
            `/guardian/${subAccountId}/message/messages/${guardianMessageId}`,
        )

        return data
    },

    getMessagesForGrid: async (
        gridPayload: IGridPayloadRequest,
        subAccountId: string,
    ): Promise<IGridResponse<IGirdMessageItem>> => {
        const query = buildQueryForGrid(gridPayload)

        const { data } = await axios.get<IGridResponse<IGirdMessageItem>>(
            `/guardian/${subAccountId}/message/messages?${query}`,
            {
                signal: gridPayload.signal,
            },
        )

        return data
    },

    getCountAllUnreadMessages: async (subAccountId: string): Promise<{ count: number }> => {
        const { data } = await axios.get<{ count: number }>(
            `/guardian/${subAccountId}/message/messages/count-unread`,
        )

        return data
    },

    getAllUnreadMessagesIds: async (subAccountId: string): Promise<{ messagesId: string[] }> => {
        const { data } = await axios.get<{ messagesId: string[] }>(
            `/guardian/${subAccountId}/message/messages/list-unread`,
        )

        return data
    },

    addAttachmentToMessage: async ({
        payload,
        guardianMessageId,
        subAccountId,
    }: {
        payload: IFileInBase64
        subAccountId: string
        guardianMessageId: string
    }): Promise<{ attachmentId: string }> => {
        const { data } = await axios.post<{ attachmentId: string }>(
            `/guardian/${subAccountId}/message/messages/${guardianMessageId}/attachment`,
            payload,
        )

        return data
    },

    removeAttachmentFromMessage: async ({
        guardianMessageId,
        subAccountId,
        attachmentId,
        version,
    }: {
        subAccountId: string
        guardianMessageId: string
        attachmentId: string
        version: number
    }): Promise<boolean> => {
        const { status } = await axios.delete(
            `/guardian/${subAccountId}/message/messages/${guardianMessageId}/attachment/${attachmentId}`,
            {
                params: {
                    version,
                },
            },
        )

        return status === NO_CONTENT_CODE
    },

    getAttachmentFromMessage: async ({
        guardianMessageId,
        attachmentId,
        subAccountId,
    }: {
        subAccountId: string
        guardianMessageId: string
        attachmentId: string
    }): Promise<IAttachmentToDownload> => {
        const { data } = await axios.get<IAttachmentToDownload>(
            `/guardian/${subAccountId}/message/messages/${guardianMessageId}/attachment/${attachmentId}`,
            {
                params: {
                    base64: true,
                },
            },
        )

        return data
    },

    sendReplyMessage: async ({
        messageId,
        attachment,
        version,
        content,
        subAccountId,
    }: {
        attachment: IFileInBase64 | null
        content: string
        version: number
        messageId: string
        subAccountId: string
    }): Promise<{ replyMessageId: string }> => {
        const { data } = await axios.post<{ replyMessageId: string }>(
            `/guardian/${subAccountId}/message/messages/${messageId}/send-reply`,
            {
                content,
                version,
                ...attachment,
            },
        )

        return data
    },

    getAttachmentFromReplyMessage: async ({
        messageId,
        attachmentId,
        subAccountId,
        replyId,
    }: {
        subAccountId: string
        messageId: string
        attachmentId: string
        replyId: string
    }): Promise<IAttachmentToDownload> => {
        const { data } = await axios.get<IAttachmentToDownload>(
            `/guardian/${subAccountId}/message/messages/${messageId}/reply/${replyId}/attachment/${attachmentId}`,
            {
                params: {
                    base64: true,
                },
            },
        )

        return data
    },
})
